import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ReceptionPortal from './pages/ReceptionPortal';
import OptometristPortal from './pages/OptometristPortal';
import DoctorPortal from './pages/DoctorPortal';
import OPDPortal from './pages/OPDPortal';
import Patients from './pages/Patients';
import Medicines from './pages/Medicines';
import Analytics from './pages/Analytics';
import Users from './pages/Users';
import Appointments from './pages/Appointments';
import Billing from './pages/Billing';
import FeeMaster from './pages/FeeMaster';
import FollowUps from './pages/FollowUps';
import ClinicSettings from './pages/ClinicSettings';
import MedicalHistoryMaster from './pages/MedicalHistoryMaster';
import PermissionMatrix from './pages/PermissionMatrix';
import Reports from './pages/Reports';
import { hasPermission } from './constants/permissions';

// Components
import CleanLayout from './components/CleanLayout';

// EyeDealist design language
// Soft pastel palette on light neutral surfaces — muted lavender accent,
// dusty status colors, flat elevation, dense tables. No bright/saturated colors.
const theme = createTheme({
  palette: {
    primary: {
      main: '#7C84D4',
      light: '#A9AFE5',
      dark: '#626AC4',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#8B90A7',
      light: '#B0B5C6',
      dark: '#6B7086',
      contrastText: '#ffffff',
    },
    success: {
      main: '#6BBD9E',
      light: '#A5DCC7',
      dark: '#4FAA87',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#DCB26E',
      light: '#EDD3A3',
      dark: '#C89D55',
      contrastText: '#ffffff',
    },
    error: {
      main: '#D98B8B',
      light: '#EBB3B3',
      dark: '#C47070',
      contrastText: '#ffffff',
    },
    info: {
      main: '#7FAFD1',
      light: '#ACCBE3',
      dark: '#5F97BD',
      contrastText: '#ffffff',
    },
    background: {
      default: '#FAFAFD',
      paper: '#ffffff',
    },
    text: {
      primary: '#3D3B54',
      secondary: '#7B7A92',
    },
    divider: '#E9E9F2',
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 14,
    h1: { fontSize: '2.125rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.01em', color: '#3D3B54' },
    h2: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.01em', color: '#3D3B54' },
    h3: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.35, color: '#3D3B54' },
    h4: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.4, color: '#3D3B54' },
    h5: { fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.4, color: '#3D3B54' },
    h6: { fontSize: '1rem', fontWeight: 700, lineHeight: 1.5, color: '#3D3B54' },
    subtitle1: { fontWeight: 600, color: '#3D3B54' },
    subtitle2: { fontWeight: 600, color: '#56546E', fontSize: '0.875rem' },
    body1: { fontSize: '0.9rem', lineHeight: 1.6, color: '#56546E' },
    body2: { fontSize: '0.8125rem', lineHeight: 1.55, color: '#7B7A92' },
    button: { fontSize: '0.8125rem', fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
    caption: { fontSize: '0.75rem', lineHeight: 1.4, color: '#9B9AB0' },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
          fontWeight: 600,
          padding: '8px 18px',
          minHeight: '38px',
          boxShadow: 'none',
          transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundColor: '#7C84D4',
          '&:hover': {
            backgroundColor: '#6C74CC',
            boxShadow: 'none',
          },
        },
        outlined: {
          borderWidth: '1px',
          borderColor: '#D8D8E8',
          color: '#56546E',
          '&:hover': {
            borderWidth: '1px',
            borderColor: '#B0B5C6',
            backgroundColor: '#F6F6FB',
          },
        },
        sizeSmall: {
          padding: '5px 12px',
          minHeight: '30px',
          fontSize: '0.78125rem',
          borderRadius: '6px',
        },
        sizeLarge: {
          padding: '10px 22px',
          minHeight: '44px',
          fontSize: '0.9375rem',
          borderRadius: '8px',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: '#ffffff',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#E9E9F2',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D8D8E8',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#A9AFE5',
              borderWidth: '1.5px',
            },
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          boxShadow: 'none',
          border: '1px solid #E9E9F2',
          transition: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '18px',
          '&:last-child': {
            paddingBottom: '18px',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
          boxShadow: '0 10px 32px -8px rgba(61, 59, 84, 0.2)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '20px 24px 14px',
          fontSize: '1.125rem',
          fontWeight: 700,
          color: '#3D3B54',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '0 24px 20px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '14px 24px 20px',
          gap: '10px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '10px 16px',
          fontSize: '0.8125rem',
          borderBottom: '1px solid #E9E9F2',
        },
        head: {
          fontWeight: 700,
          fontSize: '0.6875rem',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: '#7B7A92',
          backgroundColor: '#FAFAFD',
          borderBottom: '1px solid #E9E9F2',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: '26px',
          fontSize: '0.78125rem',
          fontWeight: 600,
          borderRadius: '6px',
        },
        sizeSmall: {
          height: '22px',
          fontSize: '0.71875rem',
          borderRadius: '5px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 2px rgba(61, 59, 84, 0.05)',
        },
        elevation2: {
          boxShadow: '0 1px 3px rgba(61, 59, 84, 0.06)',
        },
        elevation3: {
          boxShadow: '0 2px 6px rgba(61, 59, 84, 0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#3D3B54',
          boxShadow: '0 1px 2px rgba(61, 59, 84, 0.06)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid #E9E9F2',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          margin: '1px 8px',
          transition: 'background-color 0.15s ease-in-out',
          '&:hover': {
            backgroundColor: '#F4F4F9',
          },
          '&.Mui-selected': {
            backgroundColor: '#EDEFFB',
            color: '#626AC4',
            '&:hover': {
              backgroundColor: '#E2E5F6',
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: '2px',
          borderRadius: '0',
          backgroundColor: '#7C84D4',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          color: '#7B7A92',
          '&.Mui-selected': {
            color: '#626AC4',
          },
        },
      },
    },
  },
});

const PrivateRoute = ({ children, allowedRoles, requiredPerm }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // If a module permission is defined it takes precedence (enables custom
  // grants beyond the role default); otherwise fall back to the role gate.
  if (requiredPerm) {
    if (!hasPermission(user, requiredPerm)) {
      return <Navigate to="/dashboard" />;
    }
  } else if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />}
      />
      
      <Route
        path="/"
        element={
          <PrivateRoute>
            <CleanLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" />} />
        <Route
          path="dashboard"
          element={
            <PrivateRoute requiredPerm="dashboard">
              <Dashboard />
            </PrivateRoute>
          }
        />

        {/* Reception Routes */}
        <Route
          path="reception"
          element={
            <PrivateRoute allowedRoles={['reception', 'admin']} requiredPerm="patients">
              <ReceptionPortal />
            </PrivateRoute>
          }
        />

        {/* Optometrist Routes */}
        <Route
          path="optometrist"
          element={
            <PrivateRoute allowedRoles={['optometrist', 'admin']} requiredPerm="optometry">
              <OptometristPortal />
            </PrivateRoute>
          }
        />

        {/* Doctor Routes */}
        <Route
          path="doctor"
          element={
            <PrivateRoute allowedRoles={['doctor', 'admin']} requiredPerm="doctor">
              <DoctorPortal />
            </PrivateRoute>
          }
        />

        {/* OPD Routes */}
        <Route
          path="opd"
          element={
            <PrivateRoute allowedRoles={['opd', 'admin']} requiredPerm="opd">
              <OPDPortal />
            </PrivateRoute>
          }
        />

        {/* Common Routes */}
        <Route
          path="patients"
          element={
            <PrivateRoute requiredPerm="patients">
              <Patients />
            </PrivateRoute>
          }
        />

        {/* Appointments & Queue */}
        <Route
          path="appointments"
          element={
            <PrivateRoute allowedRoles={['reception', 'admin']} requiredPerm="appointments">
              <Appointments />
            </PrivateRoute>
          }
        />

        {/* Billing & Payments */}
        <Route
          path="billing"
          element={
            <PrivateRoute allowedRoles={['reception', 'opd', 'admin']} requiredPerm="billing">
              <Billing />
            </PrivateRoute>
          }
        />

        {/* Fee Master */}
        <Route
          path="fee-master"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <FeeMaster />
            </PrivateRoute>
          }
        />

        {/* Follow-up Recall */}
        <Route
          path="follow-ups"
          element={
            <PrivateRoute allowedRoles={['doctor', 'reception', 'admin']} requiredPerm="followups">
              <FollowUps />
            </PrivateRoute>
          }
        />

        {/* Clinic Settings */}
        <Route
          path="settings"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <ClinicSettings />
            </PrivateRoute>
          }
        />

        {/* Medical History Master */}
        <Route
          path="medical-history"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <MedicalHistoryMaster />
            </PrivateRoute>
          }
        />

        {/* Permission Matrix */}
        <Route
          path="permissions"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <PermissionMatrix />
            </PrivateRoute>
          }
        />

        {/* Medicine Management */}
        <Route
          path="medicines"
          element={
            <PrivateRoute allowedRoles={['doctor', 'admin']} requiredPerm="medicines">
              <Medicines />
            </PrivateRoute>
          }
        />

        {/* Analytics */}
        <Route
          path="analytics"
          element={
            <PrivateRoute requiredPerm="analytics">
              <Analytics />
            </PrivateRoute>
          }
        />

        {/* Reports */}
        <Route
          path="reports"
          element={
            <PrivateRoute requiredPerm="reports">
              <Reports />
            </PrivateRoute>
          }
        />
        
        {/* Admin Routes */}
        <Route
          path="users"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <Users />
            </PrivateRoute>
          }
        />
      </Route>
      
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
